import { merge, cloneDeep } from 'lodash';
import { ObjectUtils } from '../../_core/utils/utils.object';
import { PublicBusiness } from '../../business/business.entity';
import { CreativeUnitPackageDto } from '../models/creative-unit-package.dto';
import { CustomizationUtils } from './customization.utils';
import { LayersUtils } from './layers.utils';
import { CreativeUnitWithCustomizationsDto } from '../models/creative-unit/creative-unit.dto';
import { MergeTags } from '../../_core/utils/utils.merge-tags';
import { ProjectCartItemGroupDto } from '../../cart/models/project-cart-item-group.dto';

export class MergePropertiesUtils {
	/**
	 * Creates a state of all important package details to be used to easily traverse
	 * the state and find properties to use for merge tags.
	 */
	public static getMergeableProperties(pkg: CreativeUnitPackageDto, business?: PublicBusiness, unit?: CreativeUnitWithCustomizationsDto, activeGroup?: ProjectCartItemGroupDto, metadataContext?: { [key: string]: any }) {
		// pkg = ObjectUtils.unfreeze(pkg);
		// if(business) {
		// 	business = ObjectUtils.unfreeze(business);
		// }
		// if(unit) {
		// 	unit = ObjectUtils.unfreeze(unit);
		// }
		let customizations = CustomizationUtils.findAllCustomizations(unit?.customizations  || activeGroup?.customizations || pkg?.customizations || []);

		const state: any = {
			customizations: customizations.reduce((acc, customization) => {
				acc[customization.id] = customization;
				return acc;
			}, {}),
			layers: LayersUtils.flattenLayers(unit?.layers || pkg?.layers || []).reduce((acc, layer) => {
				acc[layer.id] = layer;
				return acc;
			}, {}),
			variables: {},
			business: business || {},
			activeCreativeUnit: unit,
			activeGroup: activeGroup || {},
			scripts: pkg?.scripts || unit?.scripts || []
		};

		// Group view (no active unit): expose the active group's ASM listing metadata under the same
		// activeCreativeUnit.metadata paths used by single-unit view, so customization visibility
		// conditions (and pkg/group variableOverrides merge tags) authored as
		// activeCreativeUnit.metadata[.Group].X resolve in group view too. Placed before variable
		// resolution so downstream variables/overrides referencing the metadata resolve (parity).
		if (!unit && activeGroup) {
			// Match the group's listing, strongest key first: the reliable items' listing id, then
			// id/instanceId, then name (the effective post-reload key — instanceId is not persisted).
			// Sequential lookups so key strength beats listing order (guards duplicate-name listings).
			// API-side callers pass a unit, so this branch does not run for them.
			const listings = pkg?.creativeUnitListingSettings?.listings ?? [];
			const groupUnitListingId = (activeGroup as any).items?.[0]?.itemData?.group?.id;
			const groupListing =
				(groupUnitListingId ? listings.find(l => l.id === groupUnitListingId) : undefined)
				|| listings.find(l => l.id === activeGroup.id || (l.instanceId != null && l.instanceId === activeGroup.id))
				|| listings.find(l => l.name === activeGroup.name);
			const groupMeta = groupListing?.metadata;
			if (groupMeta && !Array.isArray(groupMeta) && Object.keys(groupMeta).length > 0) {
				// No `id` on the synthetic unit — the DIMENSIONS guard (customization.utils) relies on that.
				state.activeCreativeUnit = {
					metadata: { ...cloneDeep(groupMeta), Group: cloneDeep(groupMeta) }
				};
			}
		}

		// ALL-tab per-row context: no unit and no group context, but the caller supplies the
		// metadata of the cart context that made a row visible. Same synthetic shape as group
		// view — no `id`, so the DIMENSIONS guard holds — placed before variable resolution so
		// downstream variables referencing the metadata resolve against this context.
		if (!state.activeCreativeUnit && metadataContext && typeof metadataContext === 'object' && !Array.isArray(metadataContext) && Object.keys(metadataContext).length > 0) {
			state.activeCreativeUnit = { metadata: cloneDeep(metadataContext) };
		}

		// console.log('State', state);

		// Iterate through each variable and try to apply merge tags to them.
		// This allows us to have downstream variables that are dependent on upstream variables.
		state.variables = this.addVariablesToStateRecursively(
			ObjectUtils.unfreeze(unit?.variables || pkg?.variables || {}),
			state
		);
		// console.log('Merged Variables', state?.variables);

		// Creative Unit variable overrides should override even customization changes.
		// If there is no unit, we are likely in an all tab/group context, so we should apply the active group's variable overrides.
		// console.log('Checking for variable overrides', unit.variables, unit.variableOverrides);
		if (unit?.variableOverrides || (!unit && activeGroup?.variableOverrides)) {
			const stateVariables = state.variables;
			const overrideVariables = ObjectUtils.unfreeze(!unit ? activeGroup?.variableOverrides : unit?.variableOverrides);

			// Iterate through each variable and try to apply merge tags to them.
			// This allows us to have downstream variables that are dependent on upstream variables.
			try {
				for (let key in overrideVariables) {
					let value = overrideVariables[key];
					if (typeof value === 'string') {
						overrideVariables[key] = MergeTags.applyMergeTagsToString(value, state);
					}
				}
			} catch (e) {
				console.log('Error applying merge tags to variables', e);
			}

			let mergedVariables = merge(stateVariables, overrideVariables);
			state.variables = this.addVariablesToStateRecursively(mergedVariables, state);
			// console.log('Merged Variables after Creative unit overrides', state?.variables);
			// console.log('Merged Variables', unit.variables);
		}

		// Merge customization value variables into the state.
		for (let customization of customizations) {

			// Test for visibility.  We do this inside the loop so that we can retest if the customization ahead of us changed something that now
			// makes this customization visible.

			// Test if the customization parent is visible.
			if (customization.parentId) {
				let parentCustomization = customizations.find(c => c.id === customization.parentId);
				if (parentCustomization && !CustomizationUtils.isCustomizationVisible(parentCustomization, undefined, state, activeGroup)) {
					continue;
				}
			}

			// Test if the customization is visible.
			if (!CustomizationUtils.isCustomizationVisible(customization, undefined, state, activeGroup)) {
				continue;
			}

			let variables = CustomizationUtils.getCustomizationVariables(customization);

			if (variables) {
				const stateVariables = state.variables;
				const mergedVariables = merge(stateVariables, variables);
				state.variables = this.addVariablesToStateRecursively(mergedVariables, state);

				// console.log('Merged Variables after customizations', state?.variables);
			}
		}

		// Now look for creative unit specific overrides.
		if (unit) {
			for (let customization of customizations) {

				// Test for visibility.  We do this inside the loop so that we can retest if the customization ahead of us changed something that now
				// makes this customization visible.

				// Test if the customization parent is visible.
				if (customization.parentId) {
					let parentCustomization = customizations.find(c => c.id === customization.parentId);
					if (parentCustomization && !CustomizationUtils.isCustomizationVisible(parentCustomization, undefined, state)) {
						continue;
					}
				}

				// Test if the customization is visible.
				if (!CustomizationUtils.isCustomizationVisible(customization, undefined, state)) {
					continue;
				}

				let variables = CustomizationUtils.getCustomizationUnitVariables(customization, unit);
				// console.log('Customization Unit Variables', customization.label, variables, customization);

				if (variables) {
					const stateVariables = state.variables;
					const mergedVariables = merge(stateVariables, variables);
					state.variables = this.addVariablesToStateRecursively(mergedVariables, state);

					// console.log('Merged Variables after customizations creative unit overrides', state?.variables);
				} else {
					// console.log('No variables found for customization', customization);
				}
			}
		}

		return state;
	}

	// Iterate through each variable and try to apply merge tags to them.
	// This allows us to have downstream variables that are dependent on upstream variables.
	public static addVariablesToStateRecursively(variables: any, state: any): any {
		const output: any = {};
		for (const key in variables) {
			if (!variables.hasOwnProperty(key)) {
				continue;
			}

			const value = variables[key];
			if (typeof value === 'string') {
				try {
					output[key] = state.variables[key] = MergeTags.applyMergeTagsToString(value, state);
				} catch (e) {
					console.warn('Error applying merge tags to variables', e);
				}
			} else if (typeof value === 'object' && value !== null) {
				output[key] = state.variables[key] = this.addVariablesToStateRecursively(value, state);
			} else {
				output[key] = state.variables[key] = value; // Fallback for primitive values
			}
		}
		return output;
	}

	public static getMergedParams(params: any, mergeTagState: any) {
		return JSON.parse(MergeTags.applyMergeTagsToString(JSON.stringify(params || {}), mergeTagState));
	}
}
